import { DateService, dateService } from '../services/date/date.facade';

/** The zone a date time is assumed to be in when it carries no offset of its own. */
const FALLBACK_TIME_ZONE: string = 'Europe/London';

/** Matches a clock time, i.e. a value that denotes a moment rather than a calendar day. */
const CLOCK: RegExp = /\d:\d\d/;

/** Matches a trailing UTC or numeric offset, e.g. '...Z', '...+01:00', '...GMT+0100'. */
const OFFSET: RegExp = /(Z|[+-]\d\d:?\d\d)$/i;

/**
 * Formats a date value for display in the viewer's own time zone, returning undefined
 * instead of throwing when the value cannot be parsed.
 *
 * Component inputs are typed as `Date` but receive whatever the consumer passes - most
 * often a string from an API payload or a custom element attribute, which is a string
 * even when it carries a timestamp. Formatting an unparsable value throws
 * `RangeError: Invalid time value`, which in `ngOnChanges` takes the whole component
 * down, so prefer this helper over formatting directly.
 *
 * Strings are parsed with `parseFormat`, then as ISO 8601, then as a timestamp, then
 * natively. A date time that states no offset is read as UK time, since that is the zone
 * our own APIs and editors work in; a date with no clock time is left as a calendar day.
 *
 * @param value The date to format
 * @param displayFormat The date-fns format to render, e.g. 'dd MMMM yyyy'
 * @param parseFormat The date-fns format to parse a string value with, e.g.
 * 'dd/MM/yyyy HH:mm:ss'
 * @returns The formatted date, or undefined when the value is empty or unparsable
 */
export function tryFormatDate(
    value: Date | string | number | null | undefined,
    displayFormat: string,
    parseFormat?: string
): string | undefined {
    const DATE: Date | undefined = parseDate(value, parseFormat);

    return DATE && dateService(DATE).format(displayFormat);
}

/** Parses a date value, returning undefined rather than an invalid date. */
function parseDate(
    value: Date | string | number | null | undefined,
    parseFormat?: string
): Date | undefined {
    if (value === null || value === undefined) return undefined;

    if (typeof value !== 'string') return valid(new Date(value));

    const TRIMMED: string = value.trim();
    const TIMESTAMP: boolean = /^-?\d+$/.test(TRIMMED);
    const DATE: Date | undefined =
        valid(parseFormat ? dateService(TRIMMED, parseFormat).toDate() : undefined) ??
        valid(DateService.parseISO(TRIMMED)) ??
        valid(new Date(TIMESTAMP ? Number(TRIMMED) : TRIMMED));

    // A zoneless date time is parsed as the viewer's local time, which is the one thing
    // it is not - reread its clock as UK time.
    const ZONELESS: boolean = !TIMESTAMP && CLOCK.test(TRIMMED) && !OFFSET.test(TRIMMED);

    return DATE && ZONELESS ? fromFallbackTimeZone(DATE) : DATE;
}

/** Returns the given date, or undefined when it is missing or an invalid date. */
function valid(date: Date | undefined): Date | undefined {
    return date && !Number.isNaN(date.getTime()) ? date : undefined;
}

/**
 * Reads the date's clock as a time in {@link FALLBACK_TIME_ZONE} rather than the viewer's
 * zone, returning the instant it actually refers to.
 */
function fromFallbackTimeZone(date: Date): Date {
    const CLOCK_AS_UTC: number = Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );
    // The offset to apply depends on the instant, which depends on the offset, so settle
    // it in two passes - the first is only wrong within an hour of a clock change.
    const ESTIMATE: Date = new Date(CLOCK_AS_UTC - offsetOf(new Date(CLOCK_AS_UTC)));

    return new Date(CLOCK_AS_UTC - offsetOf(ESTIMATE));
}

/** Returns the {@link FALLBACK_TIME_ZONE} offset at the given instant, in milliseconds. */
function offsetOf(date: Date): number {
    const WALL_TIME: string = new Intl.DateTimeFormat('sv-SE', {
        timeZone: FALLBACK_TIME_ZONE,
        dateStyle: 'short',
        timeStyle: 'medium'
    }).format(date);

    return Date.parse(`${WALL_TIME.replace(' ', 'T')}Z`) - trimToSeconds(date);
}

/** Drops the milliseconds the wall time comparison in {@link offsetOf} cannot see. */
function trimToSeconds(date: Date): number {
    return Math.trunc(date.getTime() / 1000) * 1000;
}
